/* ═══════════════════════════════════════════════════════════════
   Text run folding — the generic emphasis-canonicalisation math
   shared by ContentBlock paragraphs (schema.ts) and TableCell text
   (designer/tables.ts, harvester-engine.ts).

   ONE algorithm: a "carrier" (a paragraph or a table cell) has zero
   or more block-level emphasis flags plus an optional list of runs.
   `effectiveRunsGeneric` expands that into per-character-run emphasis;
   `foldRunsGeneric` folds it back — emphasis common to every run
   promotes to the carrier-level flag, and only the leftover mixed
   emphasis stays as `runs`. Both directions normalise through the
   same code, which is what makes a round trip an equality rather
   than an approximation.
   ═══════════════════════════════════════════════════════════════ */

export interface MarkFlags {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  highlight: boolean;
}

/** The minimal shape either ContentBlock or TableCell satisfies. */
export interface EmphasisCarrier {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  highlight?: boolean;
  runs?: RunLike[];
}

export interface RunLike {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  highlight?: boolean;
}

export const sameFlags = (a: MarkFlags, b: MarkFlags): boolean =>
  a.bold === b.bold &&
  a.italic === b.italic &&
  a.underline === b.underline &&
  a.highlight === b.highlight;

/**
 * The canonical run list for a carrier: EFFECTIVE emphasis per character,
 * adjacent identical runs merged, empty runs dropped.
 */
export function effectiveRunsGeneric(b: EmphasisCarrier): Array<{ text: string } & MarkFlags> {
  const base: MarkFlags = {
    bold: !!b.bold,
    italic: !!b.italic,
    underline: !!b.underline,
    highlight: !!b.highlight,
  };
  const src: RunLike[] = b.runs && b.runs.length ? b.runs : [{ text: b.text || '' }];
  const out: Array<{ text: string } & MarkFlags> = [];
  for (const r of src) {
    if (!r.text) continue;
    const flags: MarkFlags = {
      bold: base.bold || !!r.bold,
      italic: base.italic || !!r.italic,
      underline: base.underline || !!r.underline,
      highlight: base.highlight || !!r.highlight,
    };
    const last = out[out.length - 1];
    if (last && sameFlags(last, flags)) last.text += r.text;
    else out.push({ text: r.text, ...flags });
  }
  return out;
}

/**
 * Fold a canonical run list back into a carrier's storage shape: emphasis
 * shared by EVERY run becomes the carrier-level flag, and `runs` is emitted
 * only when something is left over.
 */
export function foldRunsGeneric(runs: Array<{ text: string } & MarkFlags>): {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  highlight?: boolean;
  runs?: RunLike[];
} {
  const text = runs.map((r) => r.text).join('');
  if (!runs.length) return { text: '' };
  const all = (k: keyof MarkFlags) => runs.every((r) => r[k]);
  const allBold = all('bold');
  const allItalic = all('italic');
  const allUnderline = all('underline');
  const allHighlight = all('highlight');

  const residual: RunLike[] = runs.map((r) => ({
    text: r.text,
    ...(r.bold && !allBold ? { bold: true } : {}),
    ...(r.italic && !allItalic ? { italic: true } : {}),
    ...(r.underline && !allUnderline ? { underline: true } : {}),
    ...(r.highlight && !allHighlight ? { highlight: true } : {}),
  }));
  const mixed = residual.some((r) => r.bold || r.italic || r.underline || r.highlight);

  return {
    text,
    ...(allBold ? { bold: true } : {}),
    ...(allItalic ? { italic: true } : {}),
    ...(allUnderline ? { underline: true } : {}),
    ...(allHighlight ? { highlight: true } : {}),
    ...(mixed ? { runs: residual } : {}),
  };
}

/** The minimal shape of a ProseMirror JSONContent text node — read generically
 *  so both schema.ts (paragraphs) and designer/tables.ts (table cells) can
 *  walk a node's `content` array into a canonical run list without depending
 *  on `@tiptap/core`'s types (this module stays framework-agnostic). */
export interface RunJSONNode {
  type?: string;
  text?: string;
  marks?: Array<{ type: string }>;
}

/** Walk a ProseMirror node's `content` (its text children + marks) into the
 *  same canonical run shape `effectiveRunsGeneric`/`foldRunsGeneric` use. */
export function nodeRunsFromJSON(
  content: RunJSONNode[] | undefined
): Array<{ text: string } & MarkFlags> {
  const out: Array<{ text: string } & MarkFlags> = [];
  for (const child of content || []) {
    if (child.type !== 'text' || !child.text) continue;
    const names = new Set((child.marks || []).map((m) => m.type));
    const flags: MarkFlags = {
      bold: names.has('bold'),
      italic: names.has('italic'),
      underline: names.has('underline'),
      highlight: names.has('highlight'),
    };
    const last = out[out.length - 1];
    if (last && sameFlags(last, flags)) last.text += child.text;
    else out.push({ text: child.text, ...flags });
  }
  return out;
}
